import { useCallback, useMemo } from "react";
import { MessagesSquare, Truck, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { containerMessageAPI } from "../../services/api";
import { useContainerMessages } from "../../hooks/useContainerMessages";
import { useSocketConnected } from "../../hooks/useSocketEvent";
import { ContainerMessageList } from "../containerOrders/ContainerMessageList";
import { ContainerMessageComposer } from "../containerOrders/ContainerMessageComposer";
import type { ContainerMessaging } from "../../types/containerMessages";
import type { Handler } from "../../types/handler";
import { handlerName } from "../../types/handler";
import HandlerBubble from "../handler/HandlerBubble";

/**
 * The office's half of a container's conversation.
 *
 * On the container's own page rather than in an inbox, because that is where
 * the answer to a driver's question is: the route, the seal, the cut-off and
 * the paperwork are all on this screen, and a dispatcher reading "the gate wants
 * a booking reference" needs the booking reference, not a chat window. The bell
 * is what gets them here — a driver's message raises a company-wide
 * notification that opens this container.
 *
 * ── Why this does not look like the rest of the page ────────────────────────
 *
 * Every other block on this form is a set of fields: static, saved on a button,
 * the same when you look away and look back. This one is a person on the other
 * end of a radio. Dressed as another grey `SectionCard` it read as a text area
 * somebody had forgotten to label, and the one thing a dispatcher most needed
 * to know about it — that it is live, and that somebody is waiting — was
 * exactly what the house style flattened out.
 *
 * So it is deliberately the loudest thing on the page: an accent rail down the
 * side, a warm header instead of the form's grey one, the driver named in it,
 * and a status lamp that reports the socket rather than decorating it. The
 * conversation itself sits on a tinted ground so it reads as a transcript
 * rather than as an input.
 */

interface Props {
  orderId: string;
  lineId: string;
  cancelled?: boolean;
  /** Who is on the truck, when execution has named somebody. */
  driverName?: string | null;
  /** The truck the container is dispatched to. */
  truck?: string | null;
  /**
   * Whose container this is, in the office.
   *
   * ── Why the handler is on this block in particular ─────────────────────
   *
   * Because this is where twelve dispatchers and one shared desk actually
   * hurts. A driver's message raises a company-wide notification — and that
   * routing is right, and must not change: a driver at a shut gate is asking
   * for *an* answer, and addressing the request to one named dispatcher who is
   * at lunch is how they wait an hour (see `createContainerMessageNotification`
   * on the server, which says exactly that). The unread counter is shared for
   * the same reason: the first dispatcher to open a thread clears it for
   * everybody.
   *
   * What that leaves is a gap this fills. Everyone is told, one person opens
   * it, the badge clears for all — and nobody knows whose container it was.
   * Naming the handler here does not re-route anything and does not make the
   * alarm quieter; it tells whoever picked it up who to hand it to.
   */
  handler?: Handler | null;
  /** Keeps whatever badge the page draws in step with the thread. */
  onMessaging?: (messaging: ContainerMessaging) => void;
}

export function ContainerMessagesSection({
  orderId,
  lineId,
  cancelled = false,
  driverName,
  truck,
  handler,
  onMessaging,
}: Props) {
  const { t } = useTranslation("containerOrders");
  const live = useSocketConnected();

  // Memoised on the two ids: an object rebuilt every render would re-run the
  // hook's effects — and one of them marks the thread read.
  const transport = useMemo(
    () => ({
      list: (params?: { before?: string; limit?: number }) =>
        containerMessageAPI.list(orderId, lineId, params),
      send: (data: Parameters<typeof containerMessageAPI.send>[2]) =>
        containerMessageAPI.send(orderId, lineId, data),
      markRead: () => containerMessageAPI.markRead(orderId, lineId),
    }),
    [orderId, lineId],
  );

  const {
    messages,
    loading,
    sending,
    error,
    hasMore,
    loadOlder,
    send,
    clearError,
  } = useContainerMessages({
    lineId,
    side: "office",
    open: true,
    transport,
    onMessaging,
  });

  const handleSend = useCallback(
    async (data: Parameters<typeof send>[0]) => {
      const ok = await send(data);
      if (!ok) {
        toast.error(error ?? t("messages.sendFailed"));
        clearError();
      }
      return ok;
    },
    [send, error, clearError, t],
  );

  return (
    <div className="overflow-hidden rounded-md border border-[#E3CDAE] bg-white shadow-sm">
      {/* The rail. Four pixels of accent down the left edge is what separates
          this from a page of identically-framed white cards at a glance —
          before a single word of the header has been read. */}
      <div className="flex">
        <div className="w-1 shrink-0 bg-gradient-to-b from-[#C97A3C] to-[#B4711F]" />

        <div className="min-w-0 flex-1">
          {/* ── Who, and whether the wire is up ────────────────────────────── */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-[#EFE0CB] bg-gradient-to-r from-[#FDF6EE] to-white px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#B4711F] text-white">
                <MessagesSquare className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h3 className="truncate text-[13px] font-semibold uppercase tracking-wide text-[#8A5A28]">
                  {t("messages.sectionTitle")}
                </h3>
                {/* The other end of the radio, named — when there is a name to
                    give. A container nobody has started has no driver yet, and
                    the line is simply left out rather than spending a row of
                    the header saying so: the message waits on the truck's
                    screen either way, so the absence changes nothing a
                    dispatcher would do. */}
                {(driverName || truck || handler) && (
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 truncate text-[11.5px] text-[#A6875F]">
                    {driverName && (
                      <span className="inline-flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {driverName}
                      </span>
                    )}
                    {truck && (
                      <span className="inline-flex items-center gap-1">
                        <Truck className="h-3 w-3" />
                        {truck}
                      </span>
                    )}
                    {/* The office end of the same radio — see `handler` above.
                        Read-only here: this block is a conversation, and a
                        control that reassigned the container from inside it
                        would be an action with no relation to its surroundings.
                        The bubble beside the order number is where it moves. */}
                    {handler && (
                      <span className="inline-flex items-center gap-1">
                        <HandlerBubble handler={handler} size="sm" silent />
                        {handlerName(handler)}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* The lamp reports the socket and nothing else — see
                `useSocketConnected`. A green dot that is green because the
                component rendered is a promise made at the exact moment it
                cannot be kept. */}
            <span
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                live
                  ? "bg-[#E6F2EC] text-[#2F7A5B]"
                  : "bg-[#F4E7E7] text-[#A63A39]"
              }`}
            >
              <span className="relative flex h-1.5 w-1.5">
                {live && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2F7A5B] opacity-75" />
                )}
                <span
                  className={`relative inline-flex h-1.5 w-1.5 rounded-full ${
                    live ? "bg-[#2F7A5B]" : "bg-[#A63A39]"
                  }`}
                />
              </span>
              {t(live ? "messages.live" : "messages.offline")}
            </span>
          </div>

          {/* ── The transcript ──────────────────────────────────────────────
              Capped rather than free-growing: this is one block among a dozen
              on a long form, and a conversation that has run for a week must
              not push the route and the charges off the bottom of the page. It
              scrolls inside its own frame and opens at its newest end.

              On a tinted ground, so the bubbles read as a record of what was
              said rather than as another white input area. */}
          <div className="bg-[#F7F6F2]">
            <ContainerMessageList
              messages={messages}
              side="office"
              loading={loading}
              hasMore={hasMore}
              onLoadOlder={loadOlder}
              emptyText={t("messages.emptyOffice")}
              className="max-h-96 min-h-28"
            />
          </div>

          <ContainerMessageComposer
            onSend={handleSend}
            sending={sending}
            disabled={cancelled}
            disabledNotice={t("messages.closedCancelledOffice")}
            placeholder={t("messages.placeholderOffice")}
          />
        </div>
      </div>
    </div>
  );
}
